import BigNumber from "bignumber.js";
import { BASE18 } from "src/configs/constance";
import NFTAuctionContract from "src/contract-logic/logic/NFTAuctionContract";
import NFTCollectionContract from "src/contract-logic/logic/NFTCollectionContract";
import NFTCoreContract from "src/contract-logic/logic/NFTCoreContract";
import { collectionSort, isNullAddress, shuffleArray } from "src/utils";
import multiCall from "src/utils/multicall";
import { SpecialCollection } from "src/utils/special-knight";
import { TravaNFTApiQuery } from "../../api-query";
import CollectionQuery from "./collection-query";

export default class AuctionMarketplace {
  static async _fetchList(auctionOrderIdSlice) {
    const auctionOrder = await multiCall(
      NFTAuctionContract.ABI,
      auctionOrderIdSlice.map((idx) => ({
        address: NFTAuctionContract.ADDRESS,
        name: "getTokenOrder",
        params: [idx],
      }))
    );
    let collectionsMetadata = await CollectionQuery.fetchBasicCollections(auctionOrderIdSlice, false);

    const auctionOrderFlattened = auctionOrder.flat();
    collectionsMetadata = collectionsMetadata.map((item, index) => ({
      ...item,
      startingBid: BigNumber(auctionOrderFlattened[index].startingBid._hex).dividedBy(BASE18).toNumber(),
      currentBid: BigNumber(auctionOrderFlattened[index].currentBid._hex).dividedBy(BASE18).toNumber(),
      endTime: parseInt(auctionOrderFlattened[index].endTime) * 1000,
      nftSeller: auctionOrderFlattened[index].nftSeller,
      bidSteps: parseInt(auctionOrderFlattened[index].bidSteps),
    }));
    collectionsMetadata = collectionsMetadata.map((i) => ({ ...i, price: i.currentBid || i.startingBid }));
    let normalCollections = collectionsMetadata.filter((item) => item.rarity <= 5);
    const specialCollections = collectionsMetadata.filter((item) => item.rarity > 5);

    let armorTokenIdArray = [];
    let helmetTokenIdArray = [];
    let shieldTokenIdArray = [];
    let weaponTokenIdArray = [];
    normalCollections.forEach((item, _) => {
      armorTokenIdArray.push(item.armorTokenId);
      helmetTokenIdArray.push(item.helmetTokenId);
      shieldTokenIdArray.push(item.shieldTokenId);
      weaponTokenIdArray.push(item.weaponTokenId);
    });

    const normalItemsCollections = await CollectionQuery.fetchNormalItems(
      armorTokenIdArray,
      helmetTokenIdArray,
      shieldTokenIdArray,
      weaponTokenIdArray
    );

    let v1 = [];
    let v2 = [];
    let counter = 0;
    for (const rawCollection of normalCollections) {
      if (rawCollection.setId == 1) v1.push({ ...rawCollection, ...normalItemsCollections[counter] });
      else if (rawCollection.setId == 2) v2.push({ ...rawCollection, ...normalItemsCollections[counter] });
      counter++;
    }
    return { v1: v1.sort(collectionSort), v2: v2.sort(collectionSort), specials: specialCollections };
  }

  static async fetchList(chunk = 500) {
    const total = await NFTAuctionContract.getTokenOnAuctionCount();
    const [auctionOrderIds] = await Promise.all([
      multiCall(
        NFTAuctionContract.ABI,
        new Array(total).fill(1).map((_, idx) => ({
          address: NFTAuctionContract.ADDRESS,
          name: "getTokenOnAunctionAtIndex",
          params: [idx],
        }))
      ),
    ]);
    const auctionOrderIdsFlattened = auctionOrderIds.flat();
    shuffleArray(auctionOrderIdsFlattened);
    const n = auctionOrderIdsFlattened.length;
    let promises = [];
    for (let i = 0; i < n; i += chunk) {
      let _slice = auctionOrderIdsFlattened.slice(i, i + chunk);
      promises.push(this._fetchList(_slice));
    }
    const result = await Promise.all(promises);
    if (result.length > 0) return result[0];
    else return { v1: [], v2: [], specials: [] };
  }

  static async fetchAuctionDetail(knightId) {
    const [auction, metadata, exp] = await Promise.all([
      NFTAuctionContract.getTokenOrder(knightId),
      NFTCollectionContract.getCollectionMetadata(knightId),
      NFTCollectionContract.getCollectionExperience(knightId),
    ]);
    const isAuctionFinished = isNullAddress(auction.nftSeller);
    if (isAuctionFinished && !metadata.armorTokenId && metadata.rarity < 6) throw new Error("Item not found!");
    let resAuctionHistory;
    let lastAuctionHistory;
    if (isAuctionFinished) {
      resAuctionHistory = await TravaNFTApiQuery.get(`/v1/auctions/end/${knightId}`);
      lastAuctionHistory = resAuctionHistory.docs[resAuctionHistory.docs.length - 1];
    }
    let res = {
      armorTokenId: parseInt(metadata.armorTokenId),
      helmetTokenId: parseInt(metadata.helmetTokenId),
      shieldTokenId: parseInt(metadata.shieldTokenId),
      weaponTokenId: parseInt(metadata.weaponTokenId),
      rarity: parseInt(metadata.rarity),
      set: metadata.set,
      id: parseInt(knightId),
      exp: parseInt(exp),
    };
    if (isAuctionFinished) {
      const lastBid = lastAuctionHistory.histories[lastAuctionHistory.histories.length - 1];
      res = {
        ...res,
        startingBid: BigNumber(lastAuctionHistory.startingBid).dividedBy(BASE18).toNumber(),
        nftSeller: lastAuctionHistory.nftSeller,
        endTime: parseInt(lastAuctionHistory.endTime),
        startTime: parseInt(lastAuctionHistory.startTime) * 1000,
        currentBidder: lastBid?.nftBidder,
        currentBid: BigNumber(lastBid?.bidPrice || lastAuctionHistory.startingBid)
          .dividedBy(BASE18)
          .toNumber(),
        bidSteps: lastAuctionHistory.histories.length,
        histories: resAuctionHistory.docs.map((i) => i.histories),
        type: "history",
        txHash: lastAuctionHistory.txHash,
      };
    } else {
      res = {
        ...res,
        startingBid: BigNumber(auction.startingBid).dividedBy(BASE18).toNumber(),
        nftSeller: auction.nftSeller,
        endTime: parseInt(auction.endTime) * 1000,
        startTime: parseInt(auction.startTime) * 1000,
        currentBidder: auction.currentBidder,
        currentBid: BigNumber(auction.currentBid).dividedBy(BASE18).toNumber(),
        bidSteps: parseInt(auction.bidSteps),
        type: "auction",
      };
    }
    if (parseInt(metadata.rarity) > 5) {
      // fetch special knight
      if (!SpecialCollection[parseInt(knightId)]) throw new Error("Item not found!");
      const tokenURI = await NFTCollectionContract.getTokenURI(knightId);
      return { ...res, rarity: "limited", metadataLink: tokenURI };
    } else {
      // fetch normal knight
      const [helmet, armor, weapon, shield] = await Promise.all([
        NFTCoreContract.getTokenMetadata(metadata.helmetTokenId),
        NFTCoreContract.getTokenMetadata(metadata.armorTokenId),
        NFTCoreContract.getTokenMetadata(metadata.weaponTokenId),
        NFTCoreContract.getTokenMetadata(metadata.shieldTokenId),
      ]);
      return {
        ...res,
        armor: { tokenId: res.armorTokenId, rarity: armor.rarity, set: armor.set, exp: parseInt(armor.exp) },
        helmet: { tokenId: res.helmetTokenId, rarity: helmet.rarity, set: helmet.set, exp: parseInt(helmet.exp) },
        shield: { tokenId: res.shieldTokenId, rarity: shield.rarity, set: shield.set, exp: parseInt(shield.exp) },
        weapon: { tokenId: res.weaponTokenId, rarity: weapon.rarity, set: weapon.set, exp: parseInt(weapon.exp) },
      };
    }
  }

  static async fetchHistoryList(params) {
    const requestParams = { ...params, limit: params.limit || 8, page: params.page || 1 };
    const res = await TravaNFTApiQuery.get("/v1/auctions/end", { params: requestParams });
    const histories = res.docs || [];
    const collectionIds = histories.map((item) => item.collectionId);
    let collectionsMetadata = await CollectionQuery.fetchBasicCollections(collectionIds, false);
    collectionsMetadata = collectionsMetadata.map((item, index) => ({
      ...item,
      startingBid: BigNumber(histories[index].startingBid).dividedBy(BASE18).toNumber(),
      currentBid: histories[index].histories.length
        ? BigNumber(histories[index].histories[histories[index].histories.length - 1].bidPrice)
            .dividedBy(BASE18)
            .toNumber()
        : 0,
      endTime: histories[index].endTime,
      bidSteps: histories[index].histories.length ? histories[index].histories.length : 0,
    }));
    collectionsMetadata = collectionsMetadata.map((i) => ({ ...i, price: i.currentBid || i.startingBid }));
    let normalCollections = collectionsMetadata.filter((item) => item.rarity <= 5);
    let specialCollections = collectionsMetadata.filter((item) => item.rarity > 5);

    let armorTokenIdArray = [];
    let helmetTokenIdArray = [];
    let shieldTokenIdArray = [];
    let weaponTokenIdArray = [];
    normalCollections.forEach((item, _) => {
      armorTokenIdArray.push(item.armorTokenId);
      helmetTokenIdArray.push(item.helmetTokenId);
      shieldTokenIdArray.push(item.shieldTokenId);
      weaponTokenIdArray.push(item.weaponTokenId);
    });

    const normalItemCollections = await CollectionQuery.fetchNormalItems(
      armorTokenIdArray,
      helmetTokenIdArray,
      shieldTokenIdArray,
      weaponTokenIdArray
    );

    let v1 = [];
    let v2 = [];
    let counter = 0;
    for (const rawCollection of normalCollections) {
      if (rawCollection.setId == 1) v1.push({ ...rawCollection, ...normalItemCollections[counter] });
      else if (rawCollection.setId == 2) v2.push({ ...rawCollection, ...normalItemCollections[counter] });
      counter++;
    }
    v1 = v1.sort((item1, item2) => {
      if (item1.rarity <= item2.rarity) return 1;
      return -1;
    });
    v2 = v2.sort((item1, item2) => {
      if (item1.rarity <= item2.rarity) return 1;
      return -1;
    });
    return { v1, v2, specials: specialCollections, total: res.totalPages };
  }
}
